import { useCallback, useEffect, useRef, useState } from 'react';
import {
  serializeSession, saveSession, loadSession, sessionMeta, sessionHasContent,
  autosaveEnabled, setAutosaveEnabled, recordRecent,
} from '../lib/map-session';

/**
 * Auto-saved map session (lib/map-session.js): saves the URL query and the imported/result layers
 * 1 s after any change, restores them when /map opens without params, and backs the File tab.
 * `recentName` is the loaded dataset/project name, recorded in the Recent list.
 */
export function useMapSession({ searchParams, setSearchParams, importedLayers, setImportedLayers, resultLayers, setResultLayers, layerStyles, setLayerStyles, recentName, showToast }) {
  const [autosave, setAutosaveState] = useState(autosaveEnabled);
  const [meta, setMeta] = useState(() => sessionMeta());
  const query = searchParams.toString();
  const project = searchParams.get('project');
  const dataset = searchParams.get('dataset');
  const startedEmpty = useRef(!project && !dataset && !searchParams.get('layers'));
  // Autosave waits for the on-mount restore attempt, so an empty map never overwrites the saved one.
  const ready = useRef(!startedEmpty.current);

  const setAutosave = useCallback((on) => { setAutosaveEnabled(on); setAutosaveState(on); }, []);

  const apply = useCallback((s) => {
    setSearchParams(s.query, { replace: true });
    if (s.imported.length) setImportedLayers((prev) => [...prev.filter((l) => !s.imported.some((x) => x.id === l.id)), ...s.imported]);
    if (s.results.length) setResultLayers((prev) => [...prev.filter((l) => !s.results.some((x) => x.id === l.id)), ...s.results]);
    if (setLayerStyles && s.layerStyles) setLayerStyles((prev) => ({ ...prev, ...s.layerStyles }));
  }, [setSearchParams, setImportedLayers, setResultLayers, setLayerStyles]);

  const restoreLast = useCallback(async () => {
    const s = await loadSession();
    if (!sessionHasContent(s)) { showToast?.('No saved session to restore', 'warning'); return false; }
    apply(s);
    showToast?.('Restored your last map session');
    return true;
  }, [apply, showToast]);

  // Restore once on mount, only when the user arrived without explicit params and autosave is on.
  const restoreRef = useRef(null);
  restoreRef.current = { apply, showToast };
  useEffect(() => {
    if (!startedEmpty.current) return undefined;
    if (!autosaveEnabled() || !sessionMeta()) { ready.current = true; return undefined; }
    let live = true;
    loadSession().then((s) => {
      if (live && sessionHasContent(s)) {
        restoreRef.current.apply(s);
        restoreRef.current.showToast?.('Restored your last map session');
      }
      ready.current = true;
    });
    return () => { live = false; };
  }, []);

  // Autosave 1 s after the last change.
  useEffect(() => {
    if (!autosave) return undefined;
    const t = setTimeout(() => {
      if (!ready.current) return;
      const s = serializeSession({ query, importedLayers, resultLayers, layerStyles });
      // An empty map (after New map, or before anything is opened) never replaces the saved
      // session: Restore last session must still be able to bring the previous work back.
      if (!sessionHasContent(s)) return;
      saveSession(s).then((ok) => { if (ok) setMeta({ at: s.at, query: s.query }); });
    }, 1000);
    return () => clearTimeout(t);
  }, [autosave, query, importedLayers, resultLayers, layerStyles]);

  // Recent list: record once the dataset/project name is known.
  useEffect(() => {
    if (recentName && (project || dataset)) recordRecent({ project, dataset, name: recentName });
  }, [project, dataset, recentName]);

  const newMap = useCallback(() => {
    setSearchParams(new URLSearchParams());
    setImportedLayers([]);
    setResultLayers([]);
    showToast?.('New map');
  }, [setSearchParams, setImportedLayers, setResultLayers, showToast]);

  const mapEmpty = !query && importedLayers.length === 0 && resultLayers.length === 0;
  const canRestore = !!meta && (meta.query || meta.at) && (mapEmpty || meta.query !== query);
  return { autosave, setAutosave, newMap, restoreLast, canRestore: !!canRestore };
}
